"use client";

import Typography from "@portal/components/atoms/v2/typography";
import Icon from "@portal/components/atoms/icons";
import usePortfolios from "@portal/hooks/usePortfolios";
import useOverview from "@portal/hooks/useOverview";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { SendMessageForResize } from "../../robot/components/helpers/SendMessageForResize";
import Button from "@portal/components/atoms/v2/buttons/button";
import { RobotTextResources } from "@portal/assets/text/RobotTextResources";
import { AskTerms, AskInfo, AskContent } from "./components";
import { ICreateAskComponentProps } from "./types";
import useIsCorporateUser from "@portal/hooks/useIsCorporateUser";
import { SelectBox, SelectOption } from "@portal/components/atoms/selectBox";
import { GetBankName } from "@portal/helpers/GetBankName";
import { IsNorne } from "@portal/helpers/IsNorne";
import { NORNE_CENTERID } from "@portal/assets/banks";
import useAdvisor from "@portal/hooks/useAdvisor";
import { IClientCreatePortfolio } from "@portal/types/api/IClientCreatePortfolio";
import useCreatePortfolio from "@portal/hooks/useCreatePortfolio";

interface IBank {
  centerId: string;
  name?: string;
}

/**
 * Component for creating a new ASK account.
 * @param params
 * @returns an interface to create an ASK account.
 */
export const CreateAskComponent = ({ centerId, isDefaultLanguage, cancelClickCallback }: ICreateAskComponentProps) => {
  const { submitCreatePortfolio } = useCreatePortfolio();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const { mutate: mutatePortfolios } = usePortfolios();
  const { mutate: mutateOverview } = useOverview();
  const [isCheckboxChecked, setIsCheckboxChecked] = useState(false);
  const [showSpinner, setShowSpinner] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [primaryButtonDisabled, setPrimaryButtonDisabled] = useState(showConfirmation || !isCheckboxChecked);
  const [error, setError] = useState("");
  const texts = isDefaultLanguage ? RobotTextResources.primary : RobotTextResources.secondary;
  const { isCorporateUser } = useIsCorporateUser();
  const { data: portfolios } = usePortfolios();
  const [bank, setBank] = useState<IBank | undefined>(centerId ? { centerId, name: GetBankName(centerId) } : undefined);
  const { isAdvisor } = useAdvisor();
  const [selectedBankError, setSelectedBankError] = useState(false);

  const banks = useMemo(() => {
    if (isAdvisor && centerId && !IsNorne(centerId)) return [{ centerId, name: GetBankName(centerId) }];
    return portfolios?.reduce((acc, curr) => {
      if (!acc.some(bank => bank.centerId === curr.centerId || (IsNorne(curr.centerId) && IsNorne(bank.centerId)))) {
        let centerId = curr.centerId;
        if (IsNorne(curr.centerId)) centerId = NORNE_CENTERID;
        acc.push({ centerId, name: GetBankName(centerId) });
      }
      return acc;
    }, [] as IBank[]);
  }, [centerId, isAdvisor, portfolios]);

  useEffect(() => {
    if (
      isAdvisor &&
      centerId &&
      IsNorne(centerId) &&
      banks?.every(bank => bank.centerId !== centerId) &&
      bank?.centerId &&
      IsNorne(bank?.centerId)
    ) {
      setBank(undefined);
    }
  }, [bank?.centerId, banks, centerId, isAdvisor]);

  const OnPrimaryButtonClick = async () => {
    setSelectedBankError(false);
    let createAtCenterId = bank?.centerId;
    if (isAdvisor && centerId && IsNorne(centerId) && banks?.every(bank => !IsNorne(bank.centerId))) {
      if (banks.length === 1) createAtCenterId = banks[0].centerId;
      else {
        setSelectedBankError(true);
        return;
      }
    }
    setShowSpinner(true);
    const request = {
      centerId: createAtCenterId,
      portfolioName: "Aksjesparekonto",
      portfolioType: "ASK",
    } as IClientCreatePortfolio;

    const response = await submitCreatePortfolio(request);

    if (response.error) {
      setError(response.error);
      setShowSpinner(false);
      setShowConfirmation(true);
    } else {
      setShowSpinner(false);
      setShowConfirmation(true);
      mutatePortfolios();
      mutateOverview();
    }
    setPrimaryButtonDisabled(true);
  };

  const OnSecondaryButtonClick = useCallback(() => {
    if (cancelClickCallback) {
      cancelClickCallback();
    }
    setShowConfirmation(false);
    setError("");
  }, [cancelClickCallback]);

  useEffect(() => {
    if (buttonRef?.current) buttonRef.current.focus();
  }, [buttonRef]);

  useEffect(() => {
    SendMessageForResize();
  }, []);

  const bankOnChange = (value: IBank) => {
    setSelectedBankError(false);
    setBank(value);
  };

  return (
    <div className="flex flex-col justify-center space-y-5">
      <div className="flex justify-center items-center pt-2.5 pb-1">
        <Icon name={"womanWithMobileRedIcon"} size={"xlarge"} />
        <Typography variant="H4">{texts.ASK_HEADING}</Typography>
      </div>
      {isCorporateUser ? (
        <div className="flex justify-center">
          <Typography variant="Body">{texts.ASK_CORPORATE_ERROR_TEXT}</Typography>
        </div>
      ) : (
        <>
          {banks && banks.length > 1 && (
            <div className="px-20">
              <Typography variant="Body">{texts.ASK_BANK_LABEL}</Typography>
              <SelectBox
                value={bank}
                header={<Typography variant="Body">{bank?.name}</Typography>}
                headerPlaceholder={<Typography variant="Body">{texts.ASK_BANK_PLACEHOLDER}</Typography>}
                onChange={bankOnChange}
              >
                {banks?.map(bank => (
                  <SelectOption key={bank.name} selectKey={bank.centerId} value={bank} spaced>
                    <Typography variant="Body">{bank.name}</Typography>
                  </SelectOption>
                ))}
              </SelectBox>
              {selectedBankError && <Typography variant="Error">{texts.ASK_CREATE_MISSING_BANK_ERROR}</Typography>}
            </div>
          )}
          <div>
            <AskTerms centerId={centerId} isDefaultLanguage={isDefaultLanguage} />
            <AskInfo isDefaultLanguage={isDefaultLanguage} />
          </div>
          <AskContent
            isDefaultLanguage={isDefaultLanguage}
            error={error}
            isCheckboxChecked={isCheckboxChecked}
            setIsCheckboxChecked={setIsCheckboxChecked}
            setPrimaryButtonDisabled={setPrimaryButtonDisabled}
            showConfirmation={showConfirmation}
          />
          <div className="flex justify-evenly py-2">
            <Button ref={buttonRef} onClick={OnSecondaryButtonClick} text={texts.TEXT_CLOSE} />
            <Button
              ref={buttonRef}
              onClick={OnPrimaryButtonClick}
              primary
              wide
              text={texts.ASK_BUTTON}
              disabled={primaryButtonDisabled}
              loading={showSpinner ? 1 : 0}
            />
          </div>
        </>
      )}
    </div>
  );
};
